import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MessageService } from "primeng/api";
import { Subject, Subscription, takeUntil } from "rxjs";
import { AppCommonModule } from "src/app/common/app-common.module";
import { ErrorService } from "src/app/common/services/error.service";
import { FileContentService } from "src/app/common/services/file-content.service";
import { GoogleCloudService } from "src/app/common/services/google-cloud.service";
import { PythonApiMlService } from "src/app/common/services/python-api-ml.service";
import { DocumentsSpaceMessage } from "src/app/modules/documents-space/documents-space.model";
import { DocumentsSpaceService } from "src/app/modules/documents-space/documents-space.service";
import { DocumentsSpaceChatLineComponent } from "src/app/modules/documents-space/documents-space-chat/documents-space-chat-line/documents-space-chat-line.component";

@Component({
  selector: "app-documents-space-chat",
  standalone: true,
  imports: [AppCommonModule, DocumentsSpaceChatLineComponent],
  providers: [MessageService],
  templateUrl: "./documents-space-chat.component.html",
  styleUrls: ["./documents-space-chat.component.scss"],
})
export class DocumentsSpaceChatComponent implements OnInit, OnDestroy {
  private readonly documentsSpaceService = inject(DocumentsSpaceService);
  private readonly pythonApiMlService = inject(PythonApiMlService);
  private readonly googleCloudService = inject(GoogleCloudService);
  private readonly messageService = inject(MessageService);
  private readonly errorService = inject(ErrorService);
  private readonly fileContentService = inject(FileContentService);
  private readonly destroySubject = new Subject<void>();
  streamSubscription?: Subscription;

  @ViewChild("scrollableRef") scrollableRef!: ElementRef;
  currentScrollPosition: number | null = null;
  @Input({ required: true }) documentsSpaceId!: string;
  @Input() isChatEnabled = true;

  messages: DocumentsSpaceMessage[] = [];
  genPending = false;
  userInput = "";
  disableScrolling = false;

  async ngOnInit(): Promise<void> {
    await this.loadChat();
    this.scrollDown();
  }

  async loadChat(): Promise<void> {
    try {
      this.messages = await this.documentsSpaceService.getMessages(this.documentsSpaceId);
    } catch (err: unknown) {
      console.error("Error in loadChat:", err);
      this.setError(err);
    }
  }

  onSend(): void {
    this.genPending = true;
    this.messages.push({
      role: "user",
      content: this.userInput,
    });
    this.messages.push({
      role: "assistant",
      content: "",
    });
    this.scrollDown();
    this.generateResponse();
  }

  /** Generates a response to the user input by streaming the response from the Python API. */
  generateResponse(): void {
    try {
      this.streamSubscription = this.pythonApiMlService
        .streamDocumentSpaceChatResponse(this.documentsSpaceId, this.userInput)
        .pipe(takeUntil(this.destroySubject)) // Clean up on component destruction
        .subscribe({
          next: (chunk: string) => {
            this.addToLastMessage(chunk); // Handle plain text chunks
          },
          error: (err) => {
            console.error("Error in PythonApiMlService streaming:", err);
            this.setError(err);
          },
          complete: () => {
            this.genPending = false;
          },
        });
    } catch (err: unknown) {
      console.error("Error in generateResponse:", err);
      this.setError(err);
    }
  }

  addToLastMessage(text: string): void {
    if (this.messages.length > 0) {
      this.messages.at(-1)!.content += text;
    }
    this.scrollDown();
  }

  setError(err: unknown): void {
    const error = err as { message?: string; stack?: string } | undefined;
    if (error) {
      this.errorService.sendError(error.stack || error.message || JSON.stringify(err));
    }
    const detail =
      err instanceof MessageEvent
        ? err.data
        : $localize`:@@assistant_chat_error_retry_message:${error?.message || String(err)} Veuillez ré-essayer`;
    this.messageService.add({
      severity: "error",
      summary: "Erreur",
      detail,
    });
    this.genPending = false;
    this.scrollDown();
  }

  resetChat(): void {
    this.stopGenerating();
    this.messageService.clear();
    this.messages = [];
    this.userInput = "";
    this.genPending = false;
  }

  stopGenerating(): void {
    this.streamSubscription?.unsubscribe();
    this.genPending = false;
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.streamSubscription?.unsubscribe();
  }

  onUserInputChanged(userInput: string): void {
    this.userInput = userInput;
  }

  scrollDown(): void {
    if (this.disableScrolling) {
      return;
    }
    setTimeout(() => {
      this.scrollableRef.nativeElement.scrollTop = this.scrollableRef.nativeElement.scrollHeight;
    }, 0);
  }

  onScroll(): void {
    const scrollingUp = this.currentScrollPosition
      ? this.scrollableRef.nativeElement.scrollTop < this.currentScrollPosition
      : false;
    if (scrollingUp) {
      this.disableScrolling = true;
    }
    const isAtBottom =
      Math.abs(
        this.scrollableRef.nativeElement.scrollHeight
          - this.scrollableRef.nativeElement.scrollTop
          - this.scrollableRef.nativeElement.clientHeight,
      ) < 10;
    if (isAtBottom) {
      this.disableScrolling = false;
    }
    this.currentScrollPosition = this.scrollableRef.nativeElement.scrollTop;
  }
}
